<template>
    <DhfFooter
        :data="footerNavContents"
        :minimal="useMinimalFooter"
        :show-cookie-settings-link="showCookieSettingsLink"
        :language-object="props?.languageObject"
    />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import DhfFooter from '@/components/dhf-footer/dhf-footer.vue';
import { useNavigationQuery } from '@/composables/useNavigation';
import { useTracking } from '@/composables/useTracking';
import type { IFooterAppProps, FooterProps } from '@/types/shared-types';
import useConfiguration from './composables/useConfiguration';

const props = withDefaults(defineProps<IFooterAppProps>(), {
    baseUrl: undefined,
    useDevNavigation: false,
    navigationData: undefined,
    minimal: false,
    isGtmEnabled: false,
    languageObject: undefined,
});

const footerNavContents = ref<FooterProps | null>(null);
const showCookieSettingsLink = ref<boolean>(false);

// For context check ECOMNOW-1654
const { setEnabled } = useTracking();
watch(
    () => props.isGtmEnabled,
    () => {
        setEnabled(props.isGtmEnabled);
    }
);

const useMinimalFooter = computed(() => {
    if (typeof props.minimal === 'boolean') return props.minimal;

    return props.minimal.toLowerCase() === 'true';
});

onMounted(() => {
    const { globalsConfig, loadConfig } = useConfiguration(props?.globalsConfig);

    loadConfig().then(() => {
        if (!globalsConfig.value) return;
        const isCookieNoticeUrl = Boolean(globalsConfig.value['module.cookieNotice.url']);
        showCookieSettingsLink.value = !isCookieNoticeUrl;
    });
});

// Headless apps may pass in the navigation object, which
// should be preferred over component fetching for SSR
if (!props.navigationData) {
    const { data } = useNavigationQuery({
        useDev: props.useDevNavigation || false,
        baseUrl: props.baseUrl,
    });

    if (data.value?.footer) footerNavContents.value = data.value.footer;

    watch(data, () => {
        if (data.value?.footer) footerNavContents.value = data.value.footer;
    });
} else {
    footerNavContents.value = props.navigationData || null;
}
</script>

<style lang="scss">
@import './styles/app.scss';
</style>
